/**
 * WebView Loading Hook
 *
 * Flutter WebViewのローディング制御を統一的に管理するフック
 *
 * 責務:
 * - データ取得完了時にFlutter側のローディングを解除
 * - ブラウザの戻る操作（bfcache）でページが再表示された時もローディングを解除
 *
 * 設計原則:
 * - ローディング解除の責任はReact側が持つ
 * - Flutter側は「ローディング開始」と「hideLoading受信」のみを管理
 * - MutationObserverでDOMの描画完了を検知してからhideLoadingを呼ぶ
 */

import { useCallback, useEffect, useRef } from 'react';
import { appBridge } from '../services/bridge';

/**
 * WebViewローディング制御フック
 *
 * MutationObserverを使用してDOMの描画完了を検知し、hideLoadingを呼ぶ
 *
 * @param isLoading - データ取得中かどうか
 * @returns コンテンツのルート要素に付与するコールバックref
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const { data, isLoading } = useMyData();
 *   const contentRef = useWebViewLoading(isLoading);
 *
 *   if (isLoading) {
 *     return <div className="loading" />;
 *   }
 *
 *   return (
 *     <div ref={contentRef} className="content">
 *       {data}
 *     </div>
 *   );
 * }
 * ```
 */
export const useWebViewLoading = (isLoading: boolean): ((node: HTMLElement | null) => void) => {
  // 初回マウント時のローディング完了フラグ
  const hasInitiallyLoaded = useRef(false);
  // hideLoadingが呼ばれたかどうか（重複呼び出し防止）
  const hideLoadingCalled = useRef(false);
  // DOMノードへの参照を保持
  const nodeRef = useRef<HTMLElement | null>(null);
  // MutationObserverの参照を保持
  const observerRef = useRef<MutationObserver | null>(null);

  // hideLoading関数をメモ化
  const hideLoading = useCallback(() => {
    if (!hideLoadingCalled.current) {
      hideLoadingCalled.current = true;
      appBridge.hideLoading();
      hasInitiallyLoaded.current = true;
    }
  }, []);

  // isLoadingがtrueに戻った場合の処理
  useEffect(() => {
    if (isLoading) {
      // ローディング中はobserverを停止
      observerRef.current?.disconnect();
      hideLoadingCalled.current = false;
    }
  }, [isLoading]);

  // ページが再表示された時（bfcache/戻る操作）にhideLoadingを呼ぶ
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // persisted: bfcacheからの復元かどうか
      // 初回ロード完了後のみ処理
      if (event.persisted && hasInitiallyLoaded.current) {
        appBridge.hideLoading();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // コールバックref: MutationObserverでDOM変更を監視し、描画完了を検知
  const contentRef = useCallback((node: HTMLElement | null) => {
    nodeRef.current = node;

    if (!node) {
      // アンマウント時はobserverを停止
      observerRef.current?.disconnect();
      observerRef.current = null;
      return;
    }

    // ローディング中は設定しない
    if (isLoading) {
      return;
    }

    // すでにhideLoadingが呼ばれている場合は何もしない
    if (hideLoadingCalled.current) {
      return;
    }

    // 新しいMutationObserverを作成
    const observer = new MutationObserver(() => {
      // DOMが変更されたらhideLoadingを呼び出す
      hideLoading();
      observer.disconnect();
    });

    observerRef.current = observer;

    // 監視を開始
    observer.observe(node, {
      childList: true,  // 子ノードの変更を監視
      subtree: true,    // サブツリー全体を監視
    });

    // すでにコンテンツがある場合は即座にhideLoading
    // （MutationObserverは既存のコンテンツを検知しないため）
    if (node.childNodes.length > 0) {
      hideLoading();
      observer.disconnect();
    }
  }, [isLoading, hideLoading]);

  return contentRef;
};
